import * as React from 'react'
import Page from '../../components/page/Page'
import {createStyles, Grid, Theme, withStyles, WithStyles, withTheme, WithTheme} from "@material-ui/core";
import {RouteComponentProps} from "@reach/router";
import MarginRow from "../../components/page/MarginRow";
import {inject, observer} from "mobx-react";
import UserStore from "../../stores/UserStore";
import AccountStore from "../../stores/AccountStore";
import Account from "../../model/Account"
import {observable, when} from "mobx";
import Progress from "../../components/Progress";
import TitleBar from "../../components/TitleBar";
import AccountCard from "../account/AccountCard";
import SearchBar from "material-ui-search-bar";
import TitleButton from "../../components/TitleButton";
import ControlTower, {Routes} from "../../components/ControlTower";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import FormValidator from "../../components/form/FormValidator";

const styles = (theme: Theme) => createStyles({
  root: {
    flexGrow: 1,
    width: "100%",
    justifyContent: 'top',
    alignItems: 'center',
  },
  navigationBar: {
    display: "flex",
    flex: "none",
    flexDirection: "row",
    // marginTop: theme.spacing(1),
    height: 30,
    width: "100%",
    maxWidth: 960,
    paddingRight: theme.spacing(1)
  },
  content: {
    display: 'flex',
    flexGrow: 1,
    width: "100%",
    flex: "auto",
    overflowY: "auto",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0)
  },
  card: {
    width: "100%",
    paddingTop: theme.spacing(1),
  },
  controlBar: {
    display: "flex",
    flexDirection: "row",
    marginTop: theme.spacing(1),
    height: 30,
    width: "100%",
    maxWidth: 960,
    paddingRight: theme.spacing(1)
  },
  search: {
    flexGrow: 1,
  },
  searchBar: {
    height: 30,
  },
  filter: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "left",
    marginLeft: theme.spacing(1),
    height: 30,
  },
  filterControl: {
    textAlign: "left",
    height: 30,
  },
  sortFilter: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    width: 150,
    height: 30,
  },
  accountStatusFilter: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 10,
    width: 150,
    height: 30,
  },
  actions: {
    flexGrow: 0,
    alignItems: "center",
    alignContent: "center",
    // paddingTop: 2
  },
  actionButton: {
    width: 24,
    height: 30,
    padding: 0,
    marginLeft: theme.spacing(1),
    margin: 0,
    minWidth: 64,
    color: theme.palette.secondary.contrastText,
    background: theme.palette.secondary.main,
    "&:hover": {
      background: theme.palette.secondary.dark,
    },
    "&:active": {
      background: theme.palette.secondary.light,
    },
  }
})

enum AccountStatusOptions {
  All = "All",
  Inactive = "Inactive",
  Active = "Active",
  Suspended = "Suspended",
  Deleted = "Deleted",
}

enum SortOptions {
  Name = "Name",
  Created = "Created"
}

interface IAccountsPageProps {
  userStore?: UserStore
  accountStore?: AccountStore
  progress?: Progress
}

@inject("userStore", "accountStore", "progress")
@observer
class AccountsPage extends React.Component<WithStyles<typeof styles> & RouteComponentProps & IAccountsPageProps & WithTheme> {

  @observable allAccounts: Account[] = []
  @observable accounts: Account[] = []
  @observable isLoading = true
  @observable search?: string
  @observable accountStatusFilter = AccountStatusOptions.Active
  @observable sortFilter = SortOptions.Name

  componentDidMount () {
    const { progress } = this.props
    this.isLoading = true
    progress!.show("AccountsPage")
    const { userStore, accountStore} = this.props
    when(
      () => !userStore!.isLoading,
      async () => {
        this.search = this.getFilter("search") ?? undefined
        this.accountStatusFilter = AccountStatusOptions[this.getFilter("accountStatusFilter") ?? "Active"]
        this.sortFilter = SortOptions[this.getFilter("sortFilter") ?? "Name"]
        const accounts = await accountStore!.listAccounts()
        this.allAccounts = accounts.sort((a: Account, b: Account) => { return a.name.toLowerCase().localeCompare(b.name.toLowerCase())})
        this.filterAccounts()
        this.isLoading = false
        progress!.hide("AccountsPage")
      }
    )
  }

  render() {
    const { classes } = this.props

    const title = "Accounts"

    return (
      <Page title={title}>
        <MarginRow>
          <div className={ classes.root}>
            <TitleBar title={title}>
            </TitleBar>
            <FormValidator name="filterForm">
              <div className={classes.controlBar}>
                <div className={classes.search}>
                  <SearchBar value={this.search} className={classes.searchBar}
                             onChange={this.onSearchChange} onCancelSearch={this.onCancelSearch} />
                </div>
                <div className={classes.filter}>
                  <div className={classes.filterControl}>
                    <TextFieldValidator
                      type="text"
                      className={classes.accountStatusFilter}
                      variant="outlined"
                      size="small"
                      name="accountStatusFilter"
                      label="Account Status"
                      onChange={this.onChangeAccountStatus}
                      autocompleteOptions={{
                        options: [...Object.values(AccountStatusOptions)],
                        value: this.accountStatusFilter,
                        disableClearable: true
                      }}
                    />
                  </div>
                </div>
                <div className={classes.filter}>
                  <div className={classes.filterControl}>
                    <TextFieldValidator
                      type="text"
                      className={classes.sortFilter}
                      variant="outlined"
                      size="small"
                      name="sortFilter"
                      label="Sort By"
                      onChange={this.onChangeSort}
                      autocompleteOptions={{
                        options: [...Object.values(SortOptions)],
                        value: this.sortFilter,
                        disableClearable: true
                      }}
                    />
                  </div>
                </div>
                <div className={classes.actions}>
                  <TitleButton title="+ Add" className={classes.actionButton} onClick={this.onAddAccount}/>
                </div>
              </div>
            </FormValidator>

            {this.accounts &&
              <Grid container className={classes.content} direction="row" spacing={1}>
                {this.accounts.map((a: Account) => {
                  return (
                    <Grid item sm={6} xs={12} className={classes.card} key={a.id}>
                      <AccountCard account={a} onCardAction={this.onCardAction} onEdit={this.onEdit}/>
                    </Grid>
                  )
                })}
              </Grid>
            }
          </div>
        </MarginRow>
      </Page>
    )
  }

  onSearchChange = (value: string) => {
    this.search = value
    this.saveFilter("search", value)
    this.filterAccounts()
  }

  onCancelSearch = () => {
    this.search = undefined
    this.clearFilter("search")
    this.filterAccounts()
  }

  onChangeAccountStatus = (event: any) => {
    this.accountStatusFilter = event.target.value
    this.saveFilter(event.target.name, event.target.value)
    this.filterAccounts()
  }

  onChangeSort = (event: any) => {
    this.sortFilter = event.target.value
    this.saveFilter(event.target.name, event.target.value)
    this.filterAccounts()
  }

  onAddAccount = () => {
    ControlTower.route(`${Routes.accountsEdit}`)
  }

  saveFilter = (name: string, value: string) => {
    localStorage.setItem(`${Routes.accounts}/${name}`, value)
  }

  getFilter = (name: string) => {
    return localStorage.getItem(`${Routes.accounts}/${name}`)
  }

  clearFilter = (name: string) => {
    localStorage.removeItem(`${Routes.accounts}/${name}`)
  }

  filterAccounts = () => {
    let accounts: Account[] = []

    if (this.search || this.accountStatusFilter !== AccountStatusOptions.All) {
      this.allAccounts.forEach((a: Account) => {
        if (this.search) {
          const search = this.search.toLowerCase()
            if (a.name.toLowerCase().indexOf(search!) >= 0) {
              if (this.accountStatusFilter === AccountStatusOptions.All ||
                (this.accountStatusFilter.toString() === a.accountStatus.toString())) {
                accounts.push(a)
              }
            }
        } else if (this.accountStatusFilter === AccountStatusOptions.All ||
                  (this.accountStatusFilter.toString() === a.accountStatus.toString())) {
                  accounts.push(a)
        }
      })
    } else {
      accounts = [...this.allAccounts]
    }

    if (this.sortFilter === SortOptions.Created) {
      accounts.sort((a: Account, b: Account) => b.createdAt.localeCompare(a.createdAt))
    } else {
      accounts.sort((a: Account, b: Account) => a.name.localeCompare(b.name))
    }

    this.accounts = accounts
  }

  onCardAction = async (account: Account) => {
    ControlTower.route(`${Routes.account}/${account.id}`)
  }

  onEdit = async (account: Account) => {
    ControlTower.route(`${Routes.accountsEdit}/${account.id}`)
  }
}

export default withTheme((withStyles(styles)(AccountsPage)))